
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { Course, CourseType } from "@/types/course";
import { getCourseTypes, getCourseById } from "@/services/courseManagementService";
import CourseImageUpload from "@/components/admin/content/CourseImageUpload";

const EditCoursePage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [courseTypes, setCourseTypes] = useState<CourseType[]>([]);
  const [course, setCourse] = useState<{
    title: string;
    description: string;
    type_id: string;
    image_url: string | null;
  }>({
    title: "",
    description: "",
    type_id: "",
    image_url: null,
  });

  useEffect(() => {
    if (!courseId) {
      navigate("/admin/courses");
      return;
    }
    
    fetchCourseData();
    fetchCourseTypes();
  }, [courseId, navigate]);

  const fetchCourseData = async () => {
    try {
      if (!courseId) return;
      
      const courseData = await getCourseById(courseId);
      if (!courseData) {
        throw new Error("Course not found");
      }
      
      setCourse({
        title: courseData.title || "",
        description: courseData.description || "",
        type_id: courseData.type_id || "",
        image_url: courseData.image_url || null,
      });
      
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching course:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not load course information. Please try again.",
      });
      navigate("/admin/courses");
    }
  };

  const fetchCourseTypes = async () => {
    try {
      const types = await getCourseTypes();
      setCourseTypes(types || []);
    } catch (error) {
      console.error("Error fetching course types:", error);
    }
  };

  const handleUpdateCourse = async () => {
    if (!courseId) return;
    
    if (!course.title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Course title is required.",
      });
      return;
    }
    
    setIsSaving(true);
    
    try {
      const { error } = await supabase
        .from("courses")
        .update({
          title: course.title,
          description: course.description,
          type_id: course.type_id || null,
          updated_at: new Date().toISOString(),
          has_pending_changes: true
        })
        .eq("id", courseId);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Course updated successfully.",
      });
      
      navigate("/admin/courses");
    } catch (error) {
      console.error("Error updating course:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update course. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpdate = (newUrl: string | null) => {
    setCourse(prev => ({ ...prev, image_url: newUrl }));
  };

  return (
    <AdminLayout 
      title="Edit Course" 
      backLink="/admin/courses"
    >
      <Card>
        <CardHeader>
          <CardTitle>Edit Course Details</CardTitle>
          <CardDescription>
            Update the course information below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <p>Loading course information...</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="space-y-4">
                <Label>Course Image</Label>
                <CourseImageUpload
                  courseId={courseId || ""}
                  currentImageUrl={course.image_url}
                  onImageUpdate={handleImageUpdate}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Recommended image size: 1200×675 pixels (16:9 aspect ratio). Max file size: 2MB.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Course Title</Label>
                <Input
                  id="title"
                  placeholder="Enter course title"
                  value={course.title}
                  onChange={(e) => 
                    setCourse({ ...course, title: e.target.value })
                  }
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Course Description</Label>
                <Textarea
                  id="description"
                  placeholder="Enter course description"
                  value={course.description}
                  onChange={(e) => 
                    setCourse({ ...course, description: e.target.value })
                  }
                  rows={4}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="type">Course Type</Label>
                <select
                  id="type"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={course.type_id}
                  onChange={(e) => 
                    setCourse({ ...course, type_id: e.target.value })
                  }
                >
                  <option value="">Select a type</option>
                  {courseTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={() => navigate("/admin/courses")}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateCourse}
            disabled={isLoading || isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </CardFooter>
      </Card>
    </AdminLayout>
  );
};

export default EditCoursePage;
